import React, { useState, useRef, useEffect } from 'react';
import { linkRegex, normalizeUrl } from '../utils/linkUtils';
import { Link, Plus } from 'lucide-react';
import EnhancedLinkEditor from './EnhancedLinkEditor';

interface DescriptionProps {
  content: string;
  className?: string;
  showLinks?: boolean;
  editable?: boolean;
  onContentChange?: (newContent: string) => void;
}

const Description: React.FC<DescriptionProps> = ({ 
  content, 
  className = "text-gray-700 leading-relaxed",
  showLinks = true,
  editable = false,
  onContentChange
}) => {
  const [showLinkButton, setShowLinkButton] = useState(false);
  const [linkButtonPosition, setLinkButtonPosition] = useState({ x: 0, y: 0 });
  const [selectedText, setSelectedText] = useState('');
  const [showLinkEditor, setShowLinkEditor] = useState(false);
  const [selectionRange, setSelectionRange] = useState<{ start: number; end: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseUp = () => {
    const selection = window.getSelection();
    if (!selection || selection.toString().trim() === '') {
      setShowLinkButton(false);
      setSelectedText('');
      setSelectionRange(null);
      return;
    }

    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    
    setSelectedText(selection.toString().trim());
    setLinkButtonPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 40
    });
    setShowLinkButton(true);

    // Calculate the position in the text for later replacement
    if (containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const relativeX = rect.left - containerRect.left;
      const relativeY = rect.top - containerRect.top;
      
      // This is a simplified approach - in a real implementation, you'd need more sophisticated text position calculation
      const textContent = containerRef.current.textContent || '';
      const selectedTextStart = textContent.indexOf(selectedText);
      if (selectedTextStart !== -1) {
        setSelectionRange({
          start: selectedTextStart,
          end: selectedTextStart + selectedText.length
        });
      }
    }
  };

  const handleLinkInsert = (url: string, text: string) => {
    if (!editable || !onContentChange) {
      setShowLinkEditor(false);
      return;
    }

    try {
      // For contenteditable, we'll insert the link directly at the cursor position
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        
        // Create the link element
        const linkElement = document.createElement('a');
        linkElement.href = url;
        linkElement.target = '_blank';
        linkElement.rel = 'noopener noreferrer';
        linkElement.className = 'text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200';
        linkElement.textContent = text || url;
        
        // Delete the selected content and insert the link
        range.deleteContents();
        range.insertNode(linkElement);
        
        // Move cursor after the link
        range.setStartAfter(linkElement);
        range.setEndAfter(linkElement);
        selection.removeAllRanges();
        selection.addRange(range);
        
        // Update the content using innerHTML to preserve HTML structure
        if (containerRef.current) {
          const newContent = containerRef.current.innerHTML || '';
          onContentChange(newContent);
        }
      }
      
      setShowLinkEditor(false);
      setShowLinkButton(false);
      setSelectedText('');
      setSelectionRange(null);
    } catch (error) {
      console.error('Error inserting link:', error);
      // Don't hide the link button on error, let user try again
      setShowLinkEditor(false);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    // Don't hide if clicking on the link button or its children
    const target = event.target as Element;
    if (target.closest('.link-button-container')) {
      return;
    }
    
    if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
      setShowLinkButton(false);
      setSelectedText('');
      setSelectionRange(null);
    }
  };

  useEffect(() => {
    if (editable) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [editable]);

  if (!content) {
    if (editable) {
      return (
        <div 
          ref={containerRef}
          className={`${className} text-gray-400 italic`}
          contentEditable={true}
          onMouseUp={handleMouseUp}
          onInput={onContentChange ? (e) => {
            const newContent = e.currentTarget.innerHTML || '';
            onContentChange(newContent);
          } : undefined}
          onFocus={(e) => {
            if (e.currentTarget.textContent === 'Enter product description. You can include links like https://example.com or www.example.com') {
              e.currentTarget.textContent = '';
              e.currentTarget.classList.remove('text-gray-400', 'italic');
              e.currentTarget.classList.add('text-gray-700');
            }
          }}
          onBlur={(e) => {
            if (!e.currentTarget.textContent || e.currentTarget.textContent.trim() === '') {
              e.currentTarget.textContent = 'Enter product description. You can include links like https://example.com or www.example.com';
              e.currentTarget.classList.add('text-gray-400', 'italic');
              e.currentTarget.classList.remove('text-gray-700');
            }
          }}
          suppressContentEditableWarning={true}
        >
          Enter product description. You can include links like https://example.com or www.example.com
        </div>
      );
    }
    return null;
  }

  if (!showLinks) {
    return (
      <div 
        ref={containerRef}
        className={className}
        contentEditable={editable}
        onMouseUp={editable ? handleMouseUp : undefined}
        onInput={editable && onContentChange ? (e) => {
          const newContent = e.currentTarget.textContent || '';
          onContentChange(newContent);
        } : undefined}
        suppressContentEditableWarning={true}
      >
        {content}
      </div>
    );
  }

  const parts = content.split(linkRegex);

  const renderedContent = parts.filter(part => part).map((part, index) => {
    if (linkRegex.test(part)) {
      const href = normalizeUrl(part);
      return (
        <a
          key={index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    return part;
  });

  return (
    <div className="relative">
      <div 
        ref={containerRef}
        className={className}
        contentEditable={editable}
        onMouseUp={editable ? handleMouseUp : undefined}
        onInput={editable && onContentChange ? (e) => {
          const newContent = e.currentTarget.innerHTML || '';
          onContentChange(newContent);
        } : undefined}
        onFocus={editable ? (e) => {
          if (e.currentTarget.textContent === 'Enter product description. You can include links like https://example.com or www.example.com') {
            e.currentTarget.textContent = '';
            e.currentTarget.classList.remove('text-gray-400', 'italic');
            e.currentTarget.classList.add('text-gray-700');
          }
        } : undefined}
        onBlur={editable ? (e) => {
          if (!e.currentTarget.textContent || e.currentTarget.textContent.trim() === '') {
            e.currentTarget.textContent = 'Enter product description. You can include links like https://example.com or www.example.com';
            e.currentTarget.classList.add('text-gray-400', 'italic');
            e.currentTarget.classList.remove('text-gray-700');
          }
        } : undefined}
        suppressContentEditableWarning={true}
      >
        {editable ? content : renderedContent}
      </div>
      
      {/* Floating Link Button */}
      {showLinkButton && editable && (
        <div
          className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-1 flex items-center space-x-1 link-button-container"
          style={{
            left: linkButtonPosition.x,
            top: linkButtonPosition.y,
            transform: 'translateX(-50%)'
          }}
        >
          <button
            onClick={() => {
              // Small delay to prevent button from disappearing immediately
              setTimeout(() => setShowLinkEditor(true), 50);
            }}
            className="p-2 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors duration-200"
            title="Add link to selected text"
          >
            <Link size={16} />
          </button>
          <button
            onClick={() => {
              setShowLinkButton(false);
              setSelectedText('');
              setSelectionRange(null);
              window.getSelection()?.removeAllRanges();
            }}
            className="p-2 text-gray-700 hover:text-red-600 hover:bg-red-50 rounded transition-colors duration-200"
            title="Cancel"
          >
            <Plus size={16} className="rotate-45" />
          </button>
        </div>
      )}

      {/* Link Editor Modal */}
      <EnhancedLinkEditor
        isOpen={showLinkEditor}
        onClose={() => setShowLinkEditor(false)}
        onLinkInsert={handleLinkInsert}
        initialText={selectedText}
      />
    </div>
  );
};

export default Description; 